/**
 * DemoCraftPrintCard — anonymous /mycraftz demo card (Checkpoint C-2).
 *
 * Wraps a single showcase book's live demo CraftPrint into the existing
 * `mc-active-card` visual so nothing in the /mycraftz aesthetic changes.
 * Renders the 8-field CraftPrint (Sutra · Nimitta · Kriya · Kala · Lakshya
 * · Phala · Vighna · Upaya) with the same day-0/3/7 progressive reveal
 * cadence a real subscriber sees inside Sadhana.
 *
 * Reveal tiers (matches revealTierForDays in demoCraftPrint.js):
 *   day 0-2  → tier 1: Sutra + Kriya only. Others show a small
 *              "appears on day 3 / day 7" reveal banner (non-punitive).
 *   day 3-6  → tier 2: + Nimitta + Kala (Lakshya is held for Day 30 / Siddhi).
 *   day 7+   → tier 3: + Phala + Vighna + Upaya (inline-editable).
 *
 * Since the demo mints a fresh installed_at on the visitor's first hit
 * (see /craftprint/demo-init), all visitors start at day 0. To let a
 * curious visitor see what unlocks without waiting a real week, a small
 * "Preview reveal cadence" segmented control lets them switch the tier
 * view locally (does NOT persist or shortcut the real mechanic).
 *
 * Edits to Phala/Vighna/Upaya (only on tier 3) go through PATCH via the
 * useDemoCraftPrint hook, persisted server-side under the visitor's
 * ephemeral install_id.
 */
import React, { useMemo, useState } from "react";
import {
  Calendar, Feather, Hourglass, Target, ScrollText, Sparkles, Save, X,
} from "lucide-react";
import { useDemoCraftPrint, revealTierForDays } from "../../lib/demoCraftPrint";
import AgedDemoPreview from "./AgedDemoPreview";

// Field metadata — labels mirror the ones on ActiveCraftPrintCard so the
// visual density on the demo matches an installed CraftPrint one-for-one.
const FIELDS = {
  tier2: [
    { key: "nimitta", label: "Nimitta", icon: Calendar,  aria: "Trigger cue" },
    { key: "kala",    label: "Kala",    icon: Hourglass, aria: "Duration" },
    // Lakshya is intentionally NOT shown on the resting card — it is held
    // back until Day 30 / the Siddhi retrospective on the real /sadhana page,
    // so the demo preview must not spoil it here either.
  ],
  tier3: [
    { key: "phala",  label: "Phala",  hint: "Fruit — what's arriving" },
    { key: "vighna", label: "Vighna", hint: "Obstacle — what's in the way" },
    { key: "upaya",  label: "Upaya",  hint: "Remedy — a small next step" },
  ],
};

function RevealBanner({ dayHint }) {
  return (
    <div className="dcp-reveal-banner" data-testid={`dcp-reveal-day-${dayHint}`}>
      <Sparkles size={11} strokeWidth={1.6} />
      <span>
        Appears on day {dayHint} of your practice — return then to name what's arriving.
      </span>
    </div>
  );
}

function EditableField({ label, hint, value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value || "");
  const [saving, setSaving]   = useState(false);
  React.useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = async () => {
    const v = draft.trim();
    if (v === (value || "").trim()) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(v);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <div className="dcp-field" data-testid={testId}>
      <div className="dcp-field-head">
        <span className="dcp-field-label">{label}</span>
        <span className="dcp-field-hint">{hint}</span>
      </div>
      {editing ? (
        <>
          <textarea
            data-testid={`${testId}-textarea`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            maxLength={400}
            className="dcp-field-textarea"
          />
          <div className="dcp-field-actions">
            <button
              type="button"
              className="dcp-btn-save"
              onClick={commit}
              disabled={saving}
              data-testid={`${testId}-save`}
            >
              <Save size={11} /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="dcp-btn-cancel"
              onClick={() => { setDraft(value || ""); setEditing(false); }}
              data-testid={`${testId}-cancel`}
            >
              <X size={11} /> Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p
            className="dcp-field-value"
            data-testid={`${testId}-text`}
          >
            {value || <em className="dcp-field-empty">—</em>}
          </p>
          <button
            type="button"
            className="dcp-field-edit-trigger"
            onClick={() => setEditing(true)}
            data-testid={`${testId}-edit`}
          >
            edit
          </button>
        </>
      )}
    </div>
  );
}

/** IdeaSwitcher — tabs across the 3 committed ideas the demo scaffolds. */
function IdeaSwitcher({ ideas, active, onPick }) {
  return (
    <div className="dcp-idea-switcher" data-testid="dcp-idea-switcher">
      {ideas.map((idea, i) => (
        <button
          type="button"
          key={idea.idea_id}
          className={`dcp-idea-tab ${active === i ? "is-active" : ""}`}
          onClick={() => onPick(i)}
          data-testid={`dcp-idea-tab-${idea.idea_id}`}
        >
          {String(i + 1).padStart(2, "0")}
        </button>
      ))}
    </div>
  );
}

/** TierPreviewToggle — lets a curious visitor peek at day-3 / day-7 unlocks
 *  without waiting real time. Purely a view-state override; does NOT
 *  shortcut the real reveal (which the hook still gates by days_since_install
 *  when the visitor returns days later).
 */
function TierPreviewToggle({ actualTier, overrideTier, onOverride }) {
  const effective = overrideTier ?? actualTier;
  const opts = [
    { label: "Day 0",  tier: 1 },
    { label: "Day 3",  tier: 2 },
    { label: "Day 7",  tier: 3 },
  ];
  return (
    <div className="dcp-tier-toggle" data-testid="dcp-tier-toggle">
      <span className="dcp-tier-toggle-label">Preview reveal:</span>
      {opts.map((o) => (
        <button
          key={o.tier}
          type="button"
          className={`dcp-tier-toggle-btn ${effective === o.tier ? "is-active" : ""}`}
          onClick={() => onOverride(o.tier === actualTier ? null : o.tier)}
          data-testid={`dcp-tier-toggle-${o.tier}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * DemoCraftPrintCard — one book, one live demo scaffold, editable in-place.
 * @param {{ book: {slug, title, author, cover?} , lang?: string }} props
 */
export default function DemoCraftPrintCard({ book, lang = "en" }) {
  const { loading, error, data, patch } = useDemoCraftPrint(book.slug, lang);
  const [activeIdx, setActiveIdx] = useState(0);
  const [tierOverride, setTierOverride] = useState(null);

  const actualTier   = revealTierForDays(data?.days_since_install || 0);
  const effectiveTier = tierOverride ?? actualTier;

  const idea = useMemo(() => {
    if (!data?.craftprints?.length) return null;
    return data.craftprints[Math.min(activeIdx, data.craftprints.length - 1)];
  }, [data, activeIdx]);

  const cover = book.cover;

  return (
    <article
      className="mc-active-card dcp-card"
      data-testid={`dcp-card-${book.slug}`}
    >
      <span
        className="dcp-demo-ribbon"
        data-testid={`dcp-ribbon-${book.slug}`}
        title="Demo — your edits are stored in this browser only"
      >
        Demo · edits saved in this browser
      </span>

      <header className="mc-active-head">
        {cover && (
          <img
            src={cover}
            alt={`${book.title} cover`}
            className="mc-active-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="mc-active-head-text">
          <p className="mc-active-eyebrow">From <em>{book.title}</em></p>
          <h3 className="mc-active-title">
            {loading ? "Preparing your CraftPrint…" : (idea?.title || "—")}
          </h3>
          <p
            className="mc-active-sutra"
            data-testid={`dcp-sutra-${book.slug}`}
          >
            {loading ? "…" : idea?.sutra ? `"${idea.sutra}"` : ""}
          </p>
        </div>
      </header>

      {error && (
        <p className="dcp-error" data-testid={`dcp-error-${book.slug}`}>
          Could not load this demo. Please refresh.
        </p>
      )}

      {!loading && !error && idea && (
        <>
          {data.craftprints.length > 1 && (
            <IdeaSwitcher
              ideas={data.craftprints}
              active={activeIdx}
              onPick={setActiveIdx}
            />
          )}

          {/* Tier 1 — Kriya is the sole action-line always visible on day 0 */}
          <dl className="mc-sutra-grid dcp-tier1-grid">
            <div>
              <dt><Feather size={13} /> Kriya</dt>
              <dd data-testid={`dcp-kriya-${book.slug}`}>{idea.kriya || "—"}</dd>
            </div>
          </dl>

          {/* Tier 2 — Nimitta / Kala. Unlocked on day 3. (Lakshya held for Siddhi.) */}
          {effectiveTier >= 2 ? (
            <dl className="mc-sutra-grid dcp-tier2-grid" data-testid={`dcp-tier2-${book.slug}`}>
              {FIELDS.tier2.map(({ key, label, icon: I }) => (
                <div key={key}>
                  <dt><I size={13} /> {label}</dt>
                  <dd data-testid={`dcp-${key}-${book.slug}`}>{idea[key] || "—"}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <RevealBanner dayHint={3} />
          )}

          {/* Tier 3 — Phala / Vighna / Upaya. Editable. Unlocked on day 7. */}
          {effectiveTier >= 3 ? (
            <div className="dcp-tier3" data-testid={`dcp-tier3-${book.slug}`}>
              {FIELDS.tier3.map(({ key, label, hint }) => (
                <EditableField
                  key={key}
                  label={label}
                  hint={hint}
                  value={idea[key]}
                  onSave={(v) => patch(idea.idea_id, { [key]: v })}
                  testId={`dcp-${key}-edit-${book.slug}`}
                />
              ))}
            </div>
          ) : (
            <RevealBanner dayHint={7} />
          )}

          <footer className="dcp-foot">
            <TierPreviewToggle
              actualTier={actualTier}
              overrideTier={tierOverride}
              onOverride={setTierOverride}
            />
            <span className="dcp-day-badge" data-testid={`dcp-day-${book.slug}`}>
              <ScrollText size={11} />
              Day {data.days_since_install}
            </span>
          </footer>

          <AgedDemoPreview slug={book.slug} />
        </>
      )}
    </article>
  );
}
